import { produce, Draft } from 'immer';
import { useCallback, useMemo } from 'react';
import type { CountCondition } from '../../../types/policy';

/**
 * Custom hook for managing count-based condition state
 * 
 * @param condition - The current count condition
 * @param onUpdate - Callback to handle updates
 * @returns An object with state and handlers for the count condition
 */
export const useCountCondition = (
  condition: CountCondition,
  onUpdate: (updatedCondition: CountCondition) => void
) => {
  // Determine if it's a value-based count condition
  const isValueCount = useMemo(() => 'value' in (condition.count || {}), [condition.count]);

  // Extract the field or value from the count object
  const countFieldOrValue = useMemo(() => {
    return isValueCount ? condition.count.value : condition.count.field;
  }, [condition.count, isValueCount]);

  // Find the comparison operator (e.g., 'greater', 'equals')
  const currentOperator = useMemo(
    () => Object.keys(condition).find((k) => k !== 'count') ?? 'equals',
    [condition]
  );

  // Get the numeric value for the comparison
  const currentValue = useMemo(
    () => String(condition[currentOperator as keyof typeof condition] ?? ''),
    [condition, currentOperator]
  );

  // Handler to toggle between 'field' and 'value' in the count object
  const handleValueCountToggle = useCallback(() => {
    onUpdate(
      produce(condition, (draft: Draft<CountCondition>) => {
        if (!draft.count) return; // Exit if no count object

        const whereClause = draft.count.where; // Store where clause

        if (isValueCount) {
          // from value to field
          const fieldValue = draft.count.value;
          draft.count = {
            field: fieldValue,
            where: whereClause,
          };
        } else {
          // from field to value
          const valueValue = draft.count.field;
          draft.count = {
            value: valueValue,
            where: whereClause,
          };
        }
      })
    );
  }, [condition, isValueCount, onUpdate]);

  // Handler for when the field/value input changes
  const handleFieldChange = useCallback(
    (newField: string) => {
      onUpdate(
        produce(condition, (draft: Draft<CountCondition>) => {
          if (draft.count) {
            const key = isValueCount ? 'value' : 'field';
            draft.count[key] = newField;
          }
        })
      );
    },
    [condition, isValueCount, onUpdate]
  );

  // Handler for when the numeric comparison value changes
  const handleValueChange = useCallback(
    (newValue: string) => {
      onUpdate(
        produce(condition, (draft: Draft<CountCondition>) => {
          const numericValue = parseInt(newValue, 10);
          if (!isNaN(numericValue)) {
            (draft as any)[currentOperator] = numericValue;
          }
        })
      );
    },
    [condition, currentOperator, onUpdate]
  );

  // Handler for when the operator changes
  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      onUpdate(
        produce(condition, (draft: Draft<CountCondition>) => {
          const value = (draft as any)[currentOperator];
          delete (draft as any)[currentOperator];
          (draft as any)[newOperator] = value;
        })
      );
    },
    [condition, currentOperator, onUpdate]
  );

  return {
    currentOperator,
    currentValue,
    countField: countFieldOrValue,
    isValueCount,
    handleFieldChange,
    handleOperatorChange,
    handleValueChange,
    handleValueCountToggle,
  };
}; 